import type { ClosedLoanDetails } from "@edge/shared";

interface Props {
  closedLoan: ClosedLoanDetails;
}

function fmtMoney(n: number | null): string {
  if (n == null) return "—";
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 2,
  }).format(n);
}

function fmtPct(n: number | null): string {
  if (n == null) return "—";
  return `${n}%`;
}

function fmtDate(iso: string | null): string {
  if (!iso) return "—";
  const d = new Date(`${iso}T12:00:00Z`);
  return new Intl.DateTimeFormat("en-US", {
    timeZone: "America/Los_Angeles",
    month: "short",
    day: "numeric",
    year: "numeric",
  }).format(d);
}

/**
 * CLOSED LOAN DETAILS — read-only table of the funded loan's final terms.
 * Rendered in OVERVIEW only for closed-loan stages (Closed Won / Funding).
 * Any null extractable field shows "—"; if any are missing, a single inline
 * hint points the user at the chat extraction workflow. "Commission Amount" is
 * the user-facing label for Zoho's Expected_Revenue; Final Commission is
 * Zoho-computed (read-only).
 */
export function ClosedLoanDetailsSection({ closedLoan: cl }: Props) {
  // The 9 extractable fields (excludes the manual Referral Fee + computed Final
  // Commission) — drives the "some values missing" hint.
  const extractable = [
    cl.finalLoanAmount,
    cl.finalRate,
    cl.finalTermMonths,
    cl.position,
    cl.lenderName,
    cl.paymentAmount,
    cl.closeDate,
    cl.loanDueDate,
    cl.commissionAmount,
  ];
  const anyMissing = extractable.some((v) => v == null);

  return (
    <div className="section-block closed-loan-section">
      <div className="section-title">
        <span className="section-title-icon">$</span>
        CLOSED LOAN DETAILS
      </div>

      <Row label="Final Loan Amount" value={fmtMoney(cl.finalLoanAmount)} highlight />
      <Row label="Final Rate" value={fmtPct(cl.finalRate)} />
      <Row
        label="Final Term"
        value={cl.finalTermMonths != null ? `${cl.finalTermMonths} mo` : "—"}
      />
      <Row label="Final Position" value={cl.position ?? "—"} />
      <Row label="Lender" value={cl.lenderName ?? "—"} />
      <Row label="Payment Amount" value={fmtMoney(cl.paymentAmount)} />
      <Row label="Close Date" value={fmtDate(cl.closeDate)} />
      <Row label="Loan Due Date" value={fmtDate(cl.loanDueDate)} />
      <Row label="Commission Amount" value={fmtMoney(cl.commissionAmount)} />
      <Row label="Referral Fee Paid" value={fmtMoney(cl.referralFeePaid)} />
      <Row
        label="Final Commission"
        value={fmtMoney(cl.finalCommission)}
        hint="auto-calculated by Zoho"
      />

      {anyMissing && (
        <div className="closed-loan-extract-hint">
          ⓘ Some details are blank. In chat, ask “find the closing email for this
          deal and extract the closed loan details” to fill them in.
        </div>
      )}
    </div>
  );
}

function Row(props: {
  label: string;
  value: string;
  highlight?: boolean;
  hint?: string;
}) {
  return (
    <div className="data-row">
      <span className="data-label">
        {props.label}
        {props.hint && <span className="data-label-hint"> ({props.hint})</span>}
      </span>
      <span
        className={["data-value", props.highlight ? "highlight" : ""]
          .filter(Boolean)
          .join(" ")}
      >
        {props.value}
      </span>
    </div>
  );
}
